import { OutpostAttemptListParams } from '../../../../../../../typings/outpost';
import { OutpostAttemptView } from '../../../../../../../typings/View.interface';
import { extractFromArray, toArray } from '../../../../utils';
import { RelativeDate, resolveRelativeDate } from '../../../../utils/date';

export interface OutpostAttemptFilters {
  date?: {
    relative?: RelativeDate;
    min?: string;
    max?: string;
  };
  status?: 'success' | 'failed';
  tenant_id?: string[];
  event_id?: string;
  destination_id?: string;
  topic?: string[];
  next?: string;
  prev?: string;
}

export interface OutpostAttemptQueryParams {
  date?: string | { min?: string | string[]; max?: string | string[]; relative?: string };
  status?: string | string[];
  tenant_id?: string | string[];
  event_id?: string | string[];
  destination_id?: string | string[];
  topic?: string | string[];
  next?: string | string[];
  prev?: string | string[];
  current_page?: string;
  selected_attempt_id?: string | string[];
  selected_history_attempt_id?: string | string[];
  dynamic_columns?: string[] | null;
}

export const DEFAULT_DATE = 'yesterday';
export const PAGE_SIZE = 50;
export const OUTPOST_ATTEMPT_INCLUDES = ['event', 'event.data', 'response_data', 'destination'];

export const extractAttemptFiltersFromQuery = (
  query: OutpostAttemptQueryParams,
): OutpostAttemptFilters => {
  let date = extractFromArray(query.date);
  if (typeof date === 'string') {
    const { relative, min, max } = resolveRelativeDate(date);
    date = {
      relative,
      min: min?.toISOString(),
      max: max?.toISOString(),
    };
  } else {
    const parsed_date = query.date as { max?: string | string[]; min?: string | string[] };
    date = {
      max: extractFromArray(parsed_date?.max) as string,
      min: extractFromArray(parsed_date?.min) as string,
    };
  }

  const status = extractFromArray(query.status) as 'success' | 'failed' | undefined;
  const event_id = extractFromArray(query.event_id) as string | undefined;
  const destination_id = extractFromArray(query.destination_id) as string | undefined;

  return {
    date,
    status,
    tenant_id: query.tenant_id ? toArray(query.tenant_id) : undefined,
    event_id,
    destination_id,
    topic: query.topic ? toArray(query.topic) : undefined,
    next: extractFromArray(query.next) as string,
    prev: extractFromArray(query.prev) as string,
  };
};

export const extractAttemptFiltersFromView = (
  current_view: OutpostAttemptView,
): OutpostAttemptFilters => {
  const filters = current_view.filters;
  return {
    ...filters,
    date: {
      relative: extractFromArray(filters.time?.relative) as RelativeDate,
      max: extractFromArray(filters.time?.max) as string,
      min: extractFromArray(filters.time?.min) as string,
    },
  };
};

export const buildOutpostAttemptListParams = (
  filters: OutpostAttemptFilters,
  extra_params: Partial<OutpostAttemptListParams> = {},
): OutpostAttemptListParams => {
  const params: OutpostAttemptListParams = {
    limit: PAGE_SIZE,
    include: OUTPOST_ATTEMPT_INCLUDES,
    ...extra_params,
  };

  if (filters.date?.relative) {
    const { min, max } = resolveRelativeDate(filters.date.relative);
    params.time = { gte: min.toISOString(), lte: max.toISOString() };
  } else if (filters.date?.min || filters.date?.max) {
    const time: { gte?: string; lte?: string } = {};
    if (filters.date.min) {
      time.gte = filters.date.min;
    }
    if (filters.date.max) {
      time.lte = filters.date.max;
    }
    params.time = time;
  }

  if (filters.status) {
    params.status = filters.status;
  }
  if (filters.tenant_id?.length) {
    params.tenant_id = filters.tenant_id;
  }
  if (filters.event_id) {
    params.event_id = filters.event_id;
  }
  if (filters.destination_id) {
    params.destination_id = filters.destination_id;
  }
  if (filters.topic?.length) {
    params.topic = filters.topic;
  }
  if (filters.next) {
    params.next = filters.next;
  }
  if (filters.prev) {
    params.prev = filters.prev;
  }

  return params;
};
